import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useSaveAnalysisVersionMutation } from '../api/useSaveAnalysisVersionMutation';
import { getAnalysisVersionPath } from '../routes';
import {
  selectCohortProfileId,
  selectServerSnapshot,
  selectSourceOrder,
  selectSources,
} from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { StagedDefinitionEdit, UseAutosaveStagedDefinitionResult } from '../types';
import { applyStagedEdit, buildStagedDefinition } from '../utils/analysisSources';

// Saves the staged definition as a new version and moves the route to it. Shared
// by the pickers, which save with one more edit applied, and the variable list,
// which stages its edit first and saves it as it is.
export const useAutosaveStagedDefinition = (
  projectId: string
): UseAutosaveStagedDefinitionResult => {
  const navigate = useNavigate();
  const { analysisProfileId, analysisVersionId } = useAnalysisRouteParams();
  const { mutate, isPending, error, reset } = useSaveAnalysisVersionMutation();

  const save = useCallback(
    (edit: StagedDefinitionEdit | null, onSaved?: () => void) => {
      // Read at call time: an edit staged just before this call has not re-rendered yet.
      const state = useAnalysisDefinitionStore.getState();
      const serverSnapshot = selectServerSnapshot(state);
      if (!analysisProfileId || !analysisVersionId || !serverSnapshot) return;
      const staged = {
        sources: selectSources(state) ?? serverSnapshot.definition?.sources ?? {},
        sourceOrder: selectSourceOrder(state),
        cohortProfileId: selectCohortProfileId(state),
      };
      mutate(
        {
          projectId,
          analysisProfileId,
          analysisVersionId,
          definition: buildStagedDefinition({
            ...(edit ? applyStagedEdit(staged, edit) : staged),
            savedDefinition: serverSnapshot.definition,
          }),
        },
        {
          onSuccess: (envelope) => {
            // `replace: true` so the back button doesn't return to the superseded version.
            navigate(
              getAnalysisVersionPath(projectId, analysisProfileId, envelope.data.analysisVersionId),
              { replace: true }
            );
            onSaved?.();
          },
        }
      );
    },
    [projectId, analysisProfileId, analysisVersionId, mutate, navigate]
  );

  const autosave = useCallback<UseAutosaveStagedDefinitionResult['autosave']>(
    (edit, onSaved) => save(edit, onSaved),
    [save]
  );
  const saveStaged = useCallback(() => save(null), [save]);

  return { autosave, saveStaged, isSaving: isPending, saveError: error, resetSaveError: reset };
};
